import React, { CSSProperties, useEffect, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { RootTheme, ScopedTheme, cnTheme } from '@yandex-360/orb-components/dist/components/Theme';
import { SegmentedControl2 } from '@yandex-360/orb-components/dist/components/SegmentedControl2';
import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { Text } from '@yandex-360/orb-components/dist/components/Text';
import { ORB_DARK } from '@yandex-360/orb-themes/orb-dark';
import { ORB_LIGHT } from '@yandex-360/orb-themes/orb-light';

import { NewTab } from '@yandex-360/iconostasis/dist/icons/NewTab';
import { Service } from '@yandex-360/global-bar';

import GlobalBar from '@/components/GlobalBar/GlobalBar';
import { MediaBlock } from '@/components/MediaBlock/MediaBlock';
import { Card, CardType } from '@/components/Card/Card';
import {
    selectServices,
    selectEnrichedServiceById,
    selectDocumentationLink,
    selectBoxVersion,
    selectLanguage,
    selectIsB2bAdmin,
    selectApplicationByKey,
} from '@/store/selectors';
import { ReduxState } from '@/store';
import { getServiceById } from './servicesData';
import { translate } from '@/i18n';

import styles from './App.module.css';

const lightThemeClassName = cnTheme(ORB_LIGHT);
const darkThemeClassName = cnTheme(ORB_DARK);

const mapStateToProps = (state: ReduxState) => ({
    services: selectServices(state),
});

const connector = connect(mapStateToProps);

type AppProps = ConnectedProps<typeof connector>;

const App: React.FC<AppProps> = ({ services }) => {
    const [selectedService, setSelectedService] = useState<Service | undefined>(services[0]);
    const currentServiceData = useSelector(selectEnrichedServiceById(selectedService));
    const documentationLink = useSelector(selectDocumentationLink);
    const boxVersion = useSelector(selectBoxVersion);
    const language = useSelector(selectLanguage);

    useEffect(() => {
        document.documentElement.lang = language;
        document.title = translate(language, 'page.title');

        const description = document.querySelector('meta[name="description"]');

        if (description) {
            description.setAttribute('content', translate(language, 'page.subtitle'));
        }
    }, [language]);

    const isB2bAdmin = useSelector(selectIsB2bAdmin);
    const adminUrl = useSelector(selectApplicationByKey('admin'))?.entrypoints?.web || '/admin';

    return (
        <RootTheme theme="light" lightThemeClassName={ lightThemeClassName } darkThemeClassName={ darkThemeClassName } brand='disk'>
            <div className={styles.app}>
                <div className={styles.globalBar}>
                    <GlobalBar />
                </div>
                <div className={styles.layout}>
                    <Text typography="caption-md-medium" as="div" margin="0" className={styles.version} color='secondary'>{translate(language, 'page.version', { version: boxVersion })}</Text>

                    <div className={styles.content}>
                        <section className={styles.top}>

                            <Text typography="heading-xl" as="h1" className='title' margin='0 0 12m 0' align='center'>{translate(language, 'page.title')}</Text>
                            <Text typography="body-lg-regular" as="p" color='secondary' margin='0 0 16m 0' align='center'>{translate(language, 'page.subtitle')}</Text>

                            <div className={styles.heroButtons}>
                                {isB2bAdmin && (
                                    <Button
                                        view='neutral'
                                        className={styles.helpButton}
                                        size='lg'
                                        iconRight={<NewTab/>}
                                        href={adminUrl}
                                        elementType={'a' as const}
                                    >
                                        {translate(language, 'page.admin')}
                                    </Button>
                                )}
                                <Button
                                    view='contrast-transparent'
                                    className={styles.helpButton}
                                    size='lg'
                                    iconRight={<NewTab/>}
                                    href={documentationLink}
                                    elementType={'a' as const}
                                >
                                    {translate(language, 'page.help')}
                                </Button>
                            </div>
                        </section>

                        {services.length === 0 ? (
                            <section className={styles.emptyState}>
                                <Text typography="heading-md" as="h2" color="secondary" align="center">
                                    {translate(language, 'page.empty.title')}
                                </Text>
                                <Text typography="body-md-regular" as="p" color="tertiary" align="center" margin="8m 0 0 0">
                                    {translate(language, 'page.empty.description')}
                                </Text>
                            </section>
                        ) : (
                            <section className={styles.services}>
                                {services.length > 1 && (
                                    <div className={styles.tabsWrapper}>
                                        <SegmentedControl2
                                            style={{
                                                '--tabs-count': services.length,
                                            } as CSSProperties}
                                            options={services.map(serviceId => {
                                                const serviceData = getServiceById(serviceId);
                                                const IconComponent = serviceData?.icon;
                                                const title = serviceData
                                                    ? translate(language, serviceData.title)
                                                    : serviceId;

                                                return {
                                                    children: <div>{title}</div>,
                                                    startSlot: IconComponent ? <IconComponent size='md' /> : null,
                                                    value: serviceId,
                                                };
                                            })}
                                            className={styles.tabs}
                                            size="lg"
                                            testId="segmented-control-2"
                                            value={selectedService}
                                            onChange={value => setSelectedService(value as Service)}
                                        />
                                    </div>
                                )}

                                {currentServiceData && (
                                    <ScopedTheme theme="light" brand={currentServiceData.brand ?? 'disk'}>
                                        <div>
                                            <MediaBlock
                                                className={styles.mediaBlock}
                                                textProps={{
                                                    title: translate(language, currentServiceData.title),
                                                    subTitle: translate(language, currentServiceData.subTitle),
                                                    buttons: [
                                                        {
                                                            children: translate(language, currentServiceData.linkText),
                                                            href: currentServiceData.entrypoints.web ?? '',
                                                            view: 'brand',
                                                            size: 'lg',
                                                            elementType: 'a',
                                                        },
                                                        ...(currentServiceData.downloadLinkTitle &&
                                                            currentServiceData.entrypoints.download_client ? [{
                                                                children: translate(
                                                                    language,
                                                                    currentServiceData.downloadLinkTitle,
                                                                ),
                                                                href: currentServiceData.entrypoints.download_client,
                                                                view: 'neutral' as const,
                                                                size: 'lg' as const,
                                                                elementType: 'a' as const,
                                                            }] : []),
                                                    ],
                                                }}
                                                media={{
                                                    image: [currentServiceData.heroImage],
                                                }}
                                                direction="content-media"
                                                mobileDirection="content-media"
                                            />

                                            <Text typography="heading-lg" as="h2" className="features-title" margin="12m 0 20m 0" align="center">{translate(language, currentServiceData.featuresTitle)}</Text>

                                            <div className={styles.featuresGrid}>
                                                {currentServiceData.features.map((feature, index) => (
                                                    <Card
                                                        key={`${currentServiceData.id}-${index}`}
                                                        className={styles.featureCard}
                                                        type={CardType.Feature}
                                                        icon={feature.icon}
                                                        title={translate(language, feature.title)}
                                                        text={translate(language, feature.text)}
                                                        bg="white"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </ScopedTheme>
                                )}
                            </section>
                        )}
                    </div>
                </div>
            </div>
        </RootTheme>
    );
};

export default connector(App);
